import { SelectOption } from 'global';

export interface PdfHeaderTemplateParams {
  reportName: string;
  accountName: string;
  lenders: SelectOption[];
  salesReps: SelectOption[];
  locations: SelectOption[];
  dateRangeLabel: string;
  logoDataUrl: string;
}

export interface PdfFooterTemplateParams {
  generatedByName: string;
  generatedAtLabel: string;
}

// Puppeteer's headerTemplate/footerTemplate render in an isolated frame with no access to
// the app's stylesheets, so all styles below must be inline and self-contained.
const CHROME_FONT_FAMILY = 'Roboto, Arial, sans-serif';
const TEXT_PRIMARY = 'rgba(0,0,0,0.87)';
const TEXT_SECONDARY = 'rgba(0,0,0,0.6)';

// 14x14 icons matching the app's existing @mui/icons-material set, embedded as raw SVG
// markup (not base64) since Puppeteer's header/footer frame can render inline SVG directly.
const buildIconSvg = (path: string): string =>
  `<svg width="14" height="14" viewBox="0 0 24 24" fill="${TEXT_SECONDARY}"><path d="${path}"/></svg>`;

// Paths correspond to @mui/icons-material's AccountBalanceOutlined, PeopleAltOutlined,
// LocationOnOutlined and DateRangeOutlined, matching the icon language used elsewhere in the app.
const ICON_LENDERS = buildIconSvg(
  'M6.5 10h-2v7h2zm6 0h-2v7h2zm8.5 9H2v2h19zm-2.5-9h-2v7h2zm-7-6.74L16.71 6H6.29zm0-2.26L2 6v2h19V6z'
);
const ICON_SALES_REPS = buildIconSvg(
  'M16.67 13.13C18.04 14.06 19 15.32 19 17v3h4v-3c0-2.18-3.57-3.47-6.33-3.87M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4c-.47 0-.91.1-1.33.24C14.5 5.27 15 6.58 15 8s-.5 2.73-1.33 3.76c.42.14.86.24 1.33.24m-6 0c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4m0-6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2m0 7c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4m6 5H3v-.99C3.2 16.29 6.3 15 9 15s5.8 1.29 6 2z'
);
const ICON_LOCATIONS = `<svg width="14" height="14" viewBox="0 0 24 24" fill="${TEXT_SECONDARY}"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7M7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 2.88-2.88 7.19-5 9.88C9.92 16.21 7 11.85 7 9"/><circle cx="12" cy="9" r="2.5"/></svg>`;
const ICON_DATE_RANGE = buildIconSvg(
  'M7 11h2v2H7zm14-5v14c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2l.01-14c0-1.1.88-2 1.99-2h1V2h2v2h8V2h2v2h1c1.1 0 2 .9 2 2M5 8h14V6H5zm14 12V10H5v10zm-4-7h2v-2h-2zm-4 0h2v-2h-2z'
);

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!);

const formatSelectOptionsLabel = (options: SelectOption[]): string =>
  options.length ? options.map(option => escapeHtml(String(option.label))).join(', ') : 'All';

const buildMetaRow = (icon: string, label: string, value: string): string => `
  <div style="display: flex; align-items: center; gap: 4px; font-size: 12px; color: ${TEXT_SECONDARY};">
    ${icon}
    <span>${label}: <span style="color: ${TEXT_PRIMARY};">${value}</span></span>
  </div>
`;

export const buildPdfHeaderTemplate = ({
  reportName,
  accountName,
  lenders,
  salesReps,
  locations,
  dateRangeLabel,
  logoDataUrl,
}: PdfHeaderTemplateParams): string =>
  `
  <div style="width: 100%; padding: 0 24px; font-family: ${CHROME_FONT_FAMILY}; box-sizing: border-box;">
    <div style="display: flex; justify-content: space-between; align-items: flex-start;">
      <div>
        <div style="font-size: 20px; font-weight: 500; color: ${TEXT_PRIMARY};">${escapeHtml(reportName)}</div>
        <div style="font-size: 16px; font-weight: 500; color: ${TEXT_PRIMARY};">${escapeHtml(accountName)}</div>
      </div>
      <img src="${logoDataUrl}" style="height: 28px;" />
    </div>
    <div style="display: flex; justify-content: space-between; margin-top: 8px;">
      <div>
        ${buildMetaRow(ICON_LENDERS, 'Lenders', formatSelectOptionsLabel(lenders))}
        ${buildMetaRow(ICON_SALES_REPS, 'Sales reps', formatSelectOptionsLabel(salesReps))}
      </div>
      <div>
        ${buildMetaRow(ICON_LOCATIONS, 'Locations', formatSelectOptionsLabel(locations))}
        ${buildMetaRow(ICON_DATE_RANGE, 'Date range', escapeHtml(dateRangeLabel))}
      </div>
    </div>
  </div>
`.replace(/(\r\n|\n|\r)/gm, '');

export const buildPdfFooterTemplate = ({ generatedByName, generatedAtLabel }: PdfFooterTemplateParams): string =>
  `
  <div style="width: 100%; padding: 8px 24px 0; font-family: ${CHROME_FONT_FAMILY}; font-size: 12px;
    color: ${TEXT_SECONDARY}; border-top: 1px solid #eee; box-sizing: border-box;
    display: flex; justify-content: space-between;">
    <span>${escapeHtml(generatedAtLabel)}</span>
    <span>Page <span class="pageNumber"></span> of <span class="totalPages"></span></span>
    <span>Generated by ${escapeHtml(generatedByName)}</span>
  </div>
`.replace(/(\r\n|\n|\r)/gm, '');
